import { LazyLoadingSpinner } from "@/components/LazyLoadingSpinner";
import { ToastProvider } from "@/context/ToastContext";
import { ETheme, FluentProvider } from "@/dependencies";
import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  ErrorComponentProps,
  Outlet,
  redirect
} from "@tanstack/react-router";
import { useEffect } from "react";
import { app } from "@microsoft/teams-js";
import { NotFound } from "@/pages/not-found";
import { DefaultCatchBoundary } from "@/pages/default-catch-boundary";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import authStore from "@/stores/auth";
import versionManagerStore from "@/stores/version";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  isAuthenticated: boolean;
}>()({
  component: () => <RootComponent />,
  notFoundComponent: NotFound,
  beforeLoad: async ({ location, context: { isAuthenticated } }) => {
    if (location.pathname === "/login") {
      const isAuthenticated = authStore.getState().isAuthenticated;
      return { isAuthenticated };
    }

    if (!isAuthenticated) {
      await authStore.getState().checkAuthStatus();
      isAuthenticated = authStore.getState().isAuthenticated;
    }

    if (!isAuthenticated) {
      throw redirect({ to: "/login" });
    }

    if (!versionManagerStore.getState().calendarVersions.length) {
      await versionManagerStore.getState().initCalendarVersions();
    }

    return { isAuthenticated };
  },

  errorComponent: (props: ErrorComponentProps) => {
    return <DefaultCatchBoundary {...props} />;
  },

  pendingComponent: () => <LazyLoadingSpinner />
});

const RootComponent: React.FC = () => {
  useEffect(() => {
    app.initialize().catch((error) => {
      console.log("Teams SDK initialization failed:", error);
    });
  }, []);

  return (
    <FluentProvider theme={ETheme.Light}>
      <ToastProvider>
        <Outlet />
      </ToastProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
    </FluentProvider>
  );
};
